import { objectSpread2 as _objectSpread2, defineProperty as _defineProperty, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import propTypes from '../../node_modules/prop-types/index.js';
import React, { useRef, useCallback } from 'react';
import classnames from '../../node_modules/classnames/index.js';
import { styled, useThemeProps, CircularProgress, Typography } from '@mui/material';
import TileGridCell from './TileGridCell.js';
import TileGridContent from './TileGridContent.js';
import ErrorMessage from '../api/ErrorMessage.js';
import { createComponentSlot } from '../../modules/styling/theme.js';

var componentRoot = createComponentSlot({
  name: "CinTileGrid",
  slot: "Root",
  skipForwardProps: ["spacing", "breakpoints"]
});

var TileGridBase = function TileGridBase(_ref) {
  var data = _ref.data,
      _ref$dataKey = _ref.dataKey,
      dataKey = _ref$dataKey === void 0 ? "key" : _ref$dataKey,
      loading = _ref.loading,
      slowLoading = _ref.slowLoading,
      error = _ref.error,
      _ref$emptyMessage = _ref.emptyMessage,
      emptyMessage = _ref$emptyMessage === void 0 ? "No Results" : _ref$emptyMessage,
      Component = _ref.component,
      render = _ref.render,
      className = _ref.className;
  var keyWarning = useRef(false);
  var renderWarning = useRef(false);
  var getItemKey = useCallback(function (item) {
    var key = item[dataKey];

    if (!key && !keyWarning.current) {
      console.warn("TileGrid dataKey is invalid");
      keyWarning.current = true;
    }

    return key;
  }, [dataKey]);
  var showLoading = loading && (slowLoading || slowLoading === undefined || !data || data.length === 0);
  var loadingContent = /*#__PURE__*/React.createElement(TileGridContent, null, /*#__PURE__*/React.createElement(CircularProgress, null));
  var errorContent = error ? /*#__PURE__*/React.createElement(TileGridContent, null, /*#__PURE__*/React.createElement(ErrorMessage, {
    error: error,
    fullWidth: true
  })) : null;
  var emptyContent = /*#__PURE__*/React.createElement(TileGridContent, null, typeof emptyMessage === "string" ? /*#__PURE__*/React.createElement(Typography, null, emptyMessage) : emptyMessage);

  if (render && Component && !renderWarning.current) {
    renderWarning.current = true;
    console.warn("TileGrid should be passed either render or component prop, not both");
  }

  var cellContent = (data !== null && data !== void 0 ? data : []).map(function (item, itemIndex) {
    var itemKey = getItemKey(item) || itemIndex;
    var tileState = {
      itemKey: itemKey,
      itemIndex: itemIndex
    };
    return /*#__PURE__*/React.createElement(TileGridCell, {
      key: itemKey
    }, render ? render(item, tileState) : Component ? /*#__PURE__*/React.createElement(Component, _extends({
      item: item
    }, tileState)) : null);
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(componentRoot.className, className)
  }, showLoading ? loadingContent : !data ? errorContent : data.length === 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, emptyContent, errorContent) : /*#__PURE__*/React.createElement(React.Fragment, null, cellContent, errorContent));
};

TileGridBase.propTypes = {
  data: propTypes.arrayOf(propTypes.any),
  dataKey: propTypes.string,
  loading: propTypes.bool,
  slowLoading: propTypes.bool,
  error: propTypes.any,
  emptyMessage: propTypes.node,
  component: propTypes.elementType,
  render: propTypes.func,
  spacing: propTypes.oneOfType([propTypes.number, propTypes.string]),
  breakpoints: propTypes.arrayOf(propTypes.number),
  className: propTypes.string,
  sx: propTypes.any,
  variant: propTypes.any
};
var TileGridRoot = styled(TileGridBase, componentRoot.styledOptions)(function (_ref2) {
  var _ref2$spacing = _ref2.spacing,
      spacing = _ref2$spacing === void 0 ? 0 : _ref2$spacing,
      breakpoints = _ref2.breakpoints,
      theme = _ref2.theme;
  var padding = typeof spacing === "number" ? theme === null || theme === void 0 ? void 0 : theme.spacing(spacing) : spacing;
  return {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "flex-start",
    margin: "calc(".concat(padding, " * -1)"),
    "> .CinTileGrid-cell": _objectSpread2({
      padding: padding
    }, !!breakpoints && breakpoints.length > 0 && breakpoints.reduce(function (acc, bp, index) {
      var minWidth = bp;
      var flexBasis = 100 / (index + 2);
      return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, "@media (min-width: ".concat(minWidth, "px)"), {
        flexBasis: "".concat(flexBasis, "%")
      }));
    }, {})),
    "> .CinTileGrid-content": {
      padding: padding
    },
    "> .CinTileGrid-cell + .CinTileGrid-content, > .CinTileGrid-content + .CinTileGrid-content": {
      marginTop: "calc(".concat(padding, " * -1)"),
      paddingTop: theme.spacing(3)
    }
  };
});
/**
 * A responsive grid of tiles
 */

var TileGrid = function TileGrid(props) {
  var rootProps = useThemeProps({
    props: props,
    name: componentRoot.name
  });
  return /*#__PURE__*/React.createElement(TileGridRoot, rootProps);
};

var TileGrid$1 = TileGrid;

export { TileGrid$1 as default };
